import { useCallback, useEffect, useState } from "react";
import { fetchCoachConnectSummary } from "../lib/stripe.js";
import { useCoachBilling } from "../context/CoachBillingContext.jsx";

export function useCoachConnectSummary() {
  const { connect, connectLoading } = useCoachBilling();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);

  const connectReady = Boolean(connect?.ready);
  const connectAccountId = connect?.accountId ?? null;

  const refetch = useCallback(async () => {
    if (!connectReady || !connectAccountId) {
      setSummary(null);
      setError(null);
      setSummaryLoading(false);
      return;
    }

    setSummaryLoading(true);
    setError(null);
    try {
      const data = await fetchCoachConnectSummary();
      setSummary(data);
    } catch (e) {
      setSummary(null);
      setError(e?.message ?? String(e));
    } finally {
      setSummaryLoading(false);
    }
  }, [connectReady, connectAccountId]);

  useEffect(() => {
    if (connectLoading) return;

    if (!connectReady || !connectAccountId) {
      setSummary(null);
      setError(null);
      setSummaryLoading(false);
      return;
    }

    let cancelled = false;
    setSummaryLoading(true);
    setError(null);

    fetchCoachConnectSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setSummary(null);
          setError(e?.message ?? String(e));
        }
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });

    return () => {
      cancelled = true;
      setSummaryLoading(false);
    };
  }, [connectLoading, connectReady, connectAccountId]);

  return {
    summary,
    connect,
    connectLoading,
    summaryLoading,
    isLoading: connectLoading || summaryLoading,
    error,
    refetch,
    connectReady,
  };
}
